import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useBlocker, useNavigate } from 'react-router-dom';

import { Box, Grid, Stack, Typography } from '@mui/material';
import { Form, Formik, useFormikContext } from 'formik';
import moment from 'moment/moment';

import useNotification from '../../../../../../core/hooks/use_notification';
import { getFoodPartnerOrderAction, getFoodPartnerOrderState } from '../../../../../../features/inventory/get_food_partner_order';
import { updateFoodPartnerOrderAction, updateFoodPartnerOrderState } from '../../../../../../features/inventory/update_food_partner_order';
import { FormRouteEnum } from '../../../../../../core/enums/common/route';
import { FoodPartnerOrderStatusEnum } from '../../../../../../core/enums/inventory/food_partner_order';
import { getAllPodsState } from '../../../../../../features/pod/get_all_pods';
import DestinationsList from '../common/destination_list';
import { SelectFieldFormik } from '../../../../../common/select_field';
import RadioGroupFormik from '../../../../../common/radio_group';
import { yesNoOptions } from '../../../../../common/form_utils';
import { DateTimePickerFormik } from '../../../../../common/date_picker';
import { GET_ROUTE_FOOD_PARTNER_ORDER, MISC_DATE_ISO_DATE_ONLY_FORMAT, MISC_DATE_ISO_FORMAT, ROUTE_FOOD_PARTNER_ORDERS } from '../../../../../../core/constants';
import { generateFoodPartnerOrderDocumentsNotificationData, getFoodPartnerOrderNotificationData, updateFoodPartnerOrderNotificationData } from '../../../../../../core/utils/notification_utils';
import { ApiRequestStatusEnum } from '../../../../../../core/enums/common/api';
import AdditionalOptions from '../common/additional_options';
import { updateAndSendFoodPartnerOrderAction, updateAndSendFoodPartnerOrderState } from '../../../../../../features/inventory/update_and_send_food_partner_order';
import UploadFiles from '../../../../common/warehouse_and_food_partner_order_form/upload_files';
import WarehouseDocumentList from './warehouse_document_list';
import { InventoryServerErrorMessageEnum } from '../../../../../../core/enums/inventory/message';
import TextFieldFormik from '../../../../../common/text_field';
import { finalizeFoodPartnerOrderAction, finalizeFoodPartnerOrderState } from '../../../../../../features/inventory/finalize_food_partner_order';
import { getAllPopsState } from '../../../../../../features/pop/get_all_pops';
import AddPodDestinationDialog from '../../../../common/warehouse_and_food_partner_order_form/add_pod_destination_dialog';
import useUnsavedChangesWarning from '../../../../../layout/component/use_unsaved_changes_warning';
import SectionPaper from '../../../../../common/section_paper';
import BottomActionBar from '../../../../../common/bottom_action_bar';
import { Button } from '../../../../../common/button';
import ConfirmDialog from '../../../../common/confirm_dialog';
import BlockerDialog from '../../../../common/blocker_dialog';
import BlockerReturnButton from '../../../../common/blocker_return_button';
import { useDirtyForm } from '../../../../../../core/context/dirty_form_context';

const FormBody = ({
                      foodPartnerOrderStatus,
                      disableForm,
                      onUpdate,
                      onGenerateWarehouseDocument,
                      onChangeSetDate,
                      getFoodPartnerOrder,
                      updateFoodPartnerOrder,
                      updateAndSendFoodPartnerOrder,
                      finalizeFoodPartnerOrder
                  }) => {
    const navigate = useNavigate();
    const formik = useFormikContext();
    useUnsavedChangesWarning(formik.dirty);
    const { setIsDirty } = useDirtyForm();

    const blocker = useBlocker(({ currentLocation, nextLocation }) =>
        formik.dirty && currentLocation.pathname !== nextLocation.pathname
    );

    const isLoading = updateFoodPartnerOrder?.loading || updateAndSendFoodPartnerOrder?.loading || finalizeFoodPartnerOrder?.loading;
    const mailAlreadySent = getFoodPartnerOrder?.data?.emailStatus !== 'UNSENT';

    useEffect(() => {
        setIsDirty(formik.dirty);

        return () => setIsDirty(false);
    }, [formik.dirty, setIsDirty]);

    return (
        <>
            <BlockerDialog blocker={ blocker }/>
            <Box component={ Form } sx={ { display: 'flex', flexDirection: 'column', gap: 3, flexGrow: 1 } }>
                <Box sx={ { flexGrow: 1, pb: 4 } }>
                    <Stack spacing={ 3 }>
                        <SectionPaper title="Dane zamówienia">
                            <Grid container spacing={ 3 }>
                                <Grid item xs={ 12 } md={ 6 } lg={ 4 }>
                                    <SelectFieldFormik
                                        name="foodPartnerId"
                                        label="Dostawca"
                                        options={ [{
                                            value: getFoodPartnerOrder?.data?.foodPartnerId,
                                            label: getFoodPartnerOrder?.data?.foodPartnerName
                                        }] }
                                        required
                                        disabled
                                    />
                                </Grid>
                                <Grid item xs={ 12 } md={ 6 } lg={ 4 }>
                                    <RadioGroupFormik
                                        name="locationDivision"
                                        label="Podział na lokalizacje"
                                        options={ yesNoOptions }
                                        disabled
                                    />
                                </Grid>
                                <Grid item xs={ 12 } md={ 6 } lg={ 4 }>
                                    <DateTimePickerFormik
                                        name="orderDate"
                                        label="Data zamówienia"
                                        required
                                        InputLabelProps={ { shrink: true } }
                                        disabled={ getFoodPartnerOrder?.data?.purposeType === 'RESERVATION' || ![FoodPartnerOrderStatusEnum.CREATED.value, FoodPartnerOrderStatusEnum.IN_PROGRESS.value].includes(foodPartnerOrderStatus) }
                                    />
                                </Grid>
                            </Grid>
                        </SectionPaper>

                        {
                            getFoodPartnerOrder?.data?.purposeType === 'STANDARD' && [FoodPartnerOrderStatusEnum.CREATED.value, FoodPartnerOrderStatusEnum.IN_PROGRESS.value].includes(foodPartnerOrderStatus) &&
                            <AdditionalOptions
                                onChangeSetDate={ (newDate, allDates) => onChangeSetDate(newDate, allDates, formik) }
                                formik={ formik }/>
                        }

                        <SectionPaper title="Lokalizacje i produkty">
                            <Stack spacing={ 2 }>
                                <DestinationsList
                                    form={ {
                                        foodPartnerId: formik?.values?.foodPartnerId,
                                        orderDate: formik?.values?.orderDate
                                    } }
                                    formik={ formik }
                                    disableForm={ disableForm }
                                    withExpirationDate
                                    foodPartnerOrderType={ getFoodPartnerOrder?.data?.purposeType }
                                    foodPartnerOrderStatus={ foodPartnerOrderStatus }
                                />
                                {
                                    getFoodPartnerOrder?.data?.purposeType === 'STANDARD' && [FoodPartnerOrderStatusEnum.CREATED.value, FoodPartnerOrderStatusEnum.IN_PROGRESS.value].includes(foodPartnerOrderStatus) &&
                                    <Box sx={ { mt: 2 } }>
                                        <AddPodDestinationDialog formik={ formik }/>
                                    </Box>
                                }
                            </Stack>
                        </SectionPaper>

                        {
                            getFoodPartnerOrder?.data?.warehouseDocuments &&
                            <WarehouseDocumentList documents={ getFoodPartnerOrder?.data?.warehouseDocuments }/>
                        }

                        <SectionPaper>
                            <Stack spacing={ 3 }>
                                <UploadFiles
                                    formik={ formik }
                                    downloadOption
                                    addAndRemoveOptions={ [FoodPartnerOrderStatusEnum.CREATED.value, FoodPartnerOrderStatusEnum.IN_PROGRESS.value].includes(foodPartnerOrderStatus) }
                                />
                                <Box>
                                    <Typography variant="subtitle2" sx={ {
                                        fontWeight: 600,
                                        mb: 1,
                                        color: 'text.secondary',
                                        textTransform: 'uppercase'
                                    } }>
                                        Notatka do zamówienia
                                    </Typography>
                                    <TextFieldFormik name="orderNote" multiline rows={ 3 } disabled={ disableForm }/>
                                </Box>
                            </Stack>
                        </SectionPaper>
                    </Stack>
                </Box>

                <BottomActionBar>
                    <Box sx={ { display: 'flex', justifyContent: 'space-between', width: '100%' } }>
                        <BlockerReturnButton formik={ formik } onReturn={ () => navigate(ROUTE_FOOD_PARTNER_ORDERS) }/>

                        <Stack direction="row" spacing={ 2 }>
                            {
                                [FoodPartnerOrderStatusEnum.CREATED.value, FoodPartnerOrderStatusEnum.IN_PROGRESS.value, FoodPartnerOrderStatusEnum.DELIVERED.value].includes(foodPartnerOrderStatus) &&
                                <ConfirmDialog
                                    isLoading={ isLoading }
                                    title="Wygenerować paczkę naklejek?"
                                    secondaryAction={ () => onUpdate(formik, false, false) }
                                    secondaryText="Nie pobieraj"
                                    primaryAction={ () => onUpdate(formik, false, true) }
                                    primaryText="Pobierz"
                                    displayIcon={ false }
                                    buttonText="Zapisz"
                                />
                            }
                            {
                                [FoodPartnerOrderStatusEnum.CREATED.value, FoodPartnerOrderStatusEnum.IN_PROGRESS.value].includes(foodPartnerOrderStatus) &&
                                <ConfirmDialog
                                    isLoading={ isLoading }
                                    maxWidth="md"
                                    title={ mailAlreadySent
                                        ? 'Czy na pewno chcesz ponownie wysłać maila do Dostawcy?'
                                        : 'Czy chcesz uwzględnić paczkę z naklejkami w mailu z zamówieniem?'
                                    }
                                    subtitle={ mailAlreadySent
                                        ? 'Pamiętaj, żeby wysyłać maila tylko przy aktualizacji zamówienia. Możesz uwzględnić paczkę z naklejkami w mailu z aktualizacją, lub wysłać ją bez naklejek.'
                                        : null
                                    }
                                    cancelText="Anuluj"
                                    secondaryAction={ () => onUpdate(formik, true, false) }
                                    secondaryText="Zapisz i wyślij bez naklejek"
                                    primaryAction={ () => onUpdate(formik, true, true) }
                                    primaryText="Zapisz i wyślij z naklejkami"
                                    displayIcon={ false }
                                    buttonText="Zapisz i wyślij"
                                />
                            }
                            {
                                foodPartnerOrderStatus === FoodPartnerOrderStatusEnum.DELIVERED.value &&
                                <Button
                                    onClick={ () => onGenerateWarehouseDocument(formik) }
                                    isLoading={ isLoading }
                                >
                                    Przyjmij na magazyn
                                </Button>
                            }
                        </Stack>
                    </Box>
                </BottomActionBar>
            </Box>
        </>
    );
};

const FormWithLocationDivision = ({ action, setAction }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { showErrorMessage, showNotification } = useNotification();

    const getAllPods = useSelector(getAllPodsState);
    const getAllPops = useSelector(getAllPopsState);
    const getFoodPartnerOrder = useSelector(getFoodPartnerOrderState);
    const updateFoodPartnerOrder = useSelector(updateFoodPartnerOrderState);
    const updateAndSendFoodPartnerOrder = useSelector(updateAndSendFoodPartnerOrderState);
    const finalizeFoodPartnerOrder = useSelector(finalizeFoodPartnerOrderState);

    const disableForm = action === FormRouteEnum.VIEW;
    const foodPartnerOrderStatus = getFoodPartnerOrder?.data?.status;

    const initialValues = useCallback(() => {
        const destinationsList = [];
        const productsList = [];

        const mainWarehouseIndex = 0;

        destinationsList.push({
            position: mainWarehouseIndex,
            name: 'Magazyn główny',
            destinationPopId: null,
            destinationPodId: null,
            canBeRemoved: false
        });
        productsList[mainWarehouseIndex] = getFoodPartnerOrder?.data?.mainWarehousePositions?.map((product, productIndex) => ({
            position: productIndex,
            name: product?.productName,
            productId: product?.productId,
            quantity: product?.quantity,
            expirationDate: moment(product?.expirationDate) || '',
            price: product?.purchaseNetPrice ? Number(product.purchaseNetPrice).toFixed(2) : ''
        }));

        Object.keys(getFoodPartnerOrder?.data?.podPositions || {})?.forEach((key, podIndex) => {
            destinationsList.push({
                position: podIndex + 1,
                name: getAllPods?.data?.map(pod => ({
                    id: pod?.id,
                    label: pod?.indoorPartner?.name + ' (' + pod?.description + ') - ' + (pod?.device?.serviceId || 'brak urządzenia')
                })).find(pod => pod?.id === key)?.label,
                destinationPodId: key,
                canBeRemoved: true
            });

            productsList[podIndex + 1] = getFoodPartnerOrder?.data?.podPositions?.[key]?.map((product, index) => ({
                position: index,
                name: product?.productName,
                productId: product?.productId,
                productBatchId: '',
                quantity: product?.quantity,
                expirationDate: product?.expirationDate ? moment(product.expirationDate) : '',
                price: product?.purchaseNetPrice ? Number(product.purchaseNetPrice).toFixed(2) : '',
                type: product?.type
            }));
        });

        Object.keys(getFoodPartnerOrder?.data?.popPositions || {})?.forEach((key, popIndex) => {
            destinationsList.push({
                position: popIndex + 1,
                name: getAllPops?.data?.map(pop => ({
                    id: pop?.id,
                    label: pop?.name
                })).find(pop => pop?.id === parseInt(key))?.label,
                destinationPopId: parseInt(key),
                canBeRemoved: true
            });

            productsList[popIndex + 1] = getFoodPartnerOrder?.data?.popPositions?.[key]?.map((product, productIndex) => ({
                position: productIndex,
                name: product?.productName,
                productId: product?.productId,
                quantity: product?.quantity,
                expirationDate: moment(product?.expirationDate) || '',
                price: product?.purchaseNetPrice || ''
            }));
        });

        return {
            foodPartnerId: getFoodPartnerOrder?.data?.foodPartnerId || '',
            locationDivision: true,
            orderDate: moment(getFoodPartnerOrder?.data?.orderDate) || '',
            productsList: productsList,
            destinationsList: destinationsList,
            attachments: getFoodPartnerOrder?.data?.attachments || [],
            attachmentIdsToDelete: [],
            orderNote: getFoodPartnerOrder?.data?.orderNote || '',
        };
    }, [getAllPods?.data, getAllPops?.data, getFoodPartnerOrder?.data]);

    const onChangeSetDate = (newDate, allDates, formik) => {
        formik?.values?.destinationsList?.forEach((_, index) => {
            const productsList = formik?.values?.productsList[index]?.map(product => {
                if (!allDates && product?.expirationDate) {
                    return product;
                }

                return ({ ...product, expirationDate: newDate });
            });

            if (productsList?.length !== 0) {
                formik.setFieldValue(`productsList.${ index }`, productsList);
            }
        });
    };

    const validateOnSubmit = async (formik) => {
        const errors = await formik.validateForm();

        if (Object.keys(errors).length > 0) {
            const touched = {
                destinationsList: true,
                productsList: formik.values.productsList.map(list =>
                    list ? list.map(() => ({
                        quantity: true,
                        price: true,
                        expirationDate: true
                    })) : []
                )
            };
            formik.setTouched(touched);
            return false;
        }
        return true;
    };

    const onUpdate = async (formik, sendEmail, generateLabels) => {
        const isValid = await validateOnSubmit(formik);
        if (!isValid) return;

        let form;

        switch (getFoodPartnerOrder?.data?.purposeType) {
            case 'STANDARD':
                const podLocationProducts = [
                    ...formik.values.destinationsList.map((destination, index) => (
                        {
                            podId: destination.destinationPodId,
                            products: [
                                ...formik?.values?.productsList?.[index]?.map(product => ({
                                    productId: product?.productId,
                                    quantity: product?.quantity,
                                    price: product?.price,
                                    expirationDate: product?.expirationDate?.format(MISC_DATE_ISO_DATE_ONLY_FORMAT),
                                }))
                            ]
                        }
                    ))
                ];
                form = {
                    locationProducts: podLocationProducts,
                    generateLabels,
                    orderDate: formik?.values?.orderDate?.format(MISC_DATE_ISO_FORMAT),
                    attachmentIdsToDelete: formik?.values?.attachmentIdsToDelete,
                    orderNote: formik?.values?.orderNote,
                };
                break;
            case 'RESERVATION':
                const popLocationProducts = [
                    ...formik.values.destinationsList.map((destination, index) => (
                        {
                            popId: destination.destinationPopId,
                            products: [
                                ...formik?.values?.productsList?.[index]?.map(product => ({
                                    productId: product?.productId,
                                    quantity: product?.quantity,
                                    price: product?.price,
                                    expirationDate: product?.expirationDate?.format(MISC_DATE_ISO_DATE_ONLY_FORMAT),
                                }))
                            ]
                        }
                    ))
                ];
                form = {
                    locationProducts: popLocationProducts,
                    generateLabels,
                    orderDate: formik?.values?.orderDate?.format(MISC_DATE_ISO_FORMAT),
                    attachmentIdsToDelete: formik?.values?.attachmentIdsToDelete,
                    orderNote: formik?.values?.orderNote,
                };
                break;
            default:
                console.warn('Unknown purposeType:', getFoodPartnerOrder?.data?.purposeType);
                form = {};
                break;
        }

        const attachments = formik?.values?.attachments || [];
        const dispatchAction = sendEmail ? updateAndSendFoodPartnerOrderAction : updateFoodPartnerOrderAction;

        dispatch(dispatchAction({ id: getFoodPartnerOrder?.data?.id, form, attachments }))
            .then(response => {
                showNotification(updateFoodPartnerOrderNotificationData(response));

                if (response?.payload?.message?.substring(0, response?.payload?.message?.indexOf(' ')) === InventoryServerErrorMessageEnum.PRODUCT_PRICE_MISMATCH.errorCode) {
                    const errors = { productsList: formik?.errors?.productsList ? [...formik.errors.productsList] : [] };
                    const productId = response?.payload?.productId;

                    formik?.values.destinationsList.forEach((_, index) => {
                        if (!errors.productsList[index]) errors.productsList[index] = [];
                        formik?.values.productsList[index].forEach((product, productIndex) => {
                            if (product?.productId === productId) {
                                errors.productsList[index][productIndex] = {
                                    ...(errors.productsList[index][productIndex] || {}),
                                    price: 'Niejednolita cena'
                                };
                            }
                        });
                    });

                    formik.setErrors(errors);
                }

                if (response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED) {
                    dispatch(getFoodPartnerOrderAction(getFoodPartnerOrder?.data?.id))
                        .then(response => showErrorMessage(getFoodPartnerOrderNotificationData(response)));
                }
            });
    };

    const onGenerateWarehouseDocument = async (formik) => {
        const isValid = await validateOnSubmit(formik);
        if (!isValid) return;

        dispatch(finalizeFoodPartnerOrderAction(getFoodPartnerOrder?.data?.id))
            .then(response => {
                showNotification(generateFoodPartnerOrderDocumentsNotificationData(response));

                if (response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED) {
                    setAction(FormRouteEnum.VIEW);
                    navigate(GET_ROUTE_FOOD_PARTNER_ORDER(getFoodPartnerOrder?.data?.id));
                }
            });
    };

    const validate = (values) => {
        const errors = {};
        const productsListErrors = [];
        let hasErrors = false;

        if (!values.destinationsList || values.destinationsList.length <= 1) {
            errors.destinationsList = 'Wybierz minimum 1 dodatkową lokalizację';
            hasErrors = true;
        }

        if (values.destinationsList && values.destinationsList.length > 0) {
            values.destinationsList.forEach((destination, index) => {
                if (destination.name === 'Magazyn główny') return;

                if (!values.productsList[index] || values.productsList[index].length === 0) {
                    productsListErrors[index] = 'Wybierz minimum 1 produkt';
                    hasErrors = true;
                } else {
                    const itemErrorsArray = [];
                    values.productsList[index].forEach((product, productIndex) => {
                        const itemErrors = {};

                        if (!product?.quantity) {
                            itemErrors.quantity = 'Pole wymagane';
                        } else if (product?.quantity <= 0) {
                            itemErrors.quantity = 'Proszę podać wartość całkowitą dodatnią';
                        }

                        if (product?.price && product?.price <= 0) {
                            itemErrors.price = 'Proszę podać wartość dodatnią';
                        }

                        if (!product?.expirationDate) {
                            itemErrors.expirationDate = 'Pole wymagane';
                        }

                        if (Object.keys(itemErrors).length > 0) {
                            itemErrorsArray[productIndex] = itemErrors;
                            hasErrors = true;
                        }
                    });

                    if (itemErrorsArray.length > 0 || productsListErrors[index]) {
                        productsListErrors[index] = productsListErrors[index] || itemErrorsArray;
                    }
                }
            });
        }

        if (hasErrors) {
            errors.productsList = productsListErrors;
        }

        return errors;
    };

    return (
        <Formik initialValues={ initialValues() } enableReinitialize={ true } validate={ validate }>
            <FormBody
                action={ action }
                setAction={ setAction }
                foodPartnerOrderStatus={ foodPartnerOrderStatus }
                disableForm={ disableForm }
                onUpdate={ onUpdate }
                onGenerateWarehouseDocument={ onGenerateWarehouseDocument }
                onChangeSetDate={ onChangeSetDate }
                getFoodPartnerOrder={ getFoodPartnerOrder }
                updateFoodPartnerOrder={ updateFoodPartnerOrder }
                updateAndSendFoodPartnerOrder={ updateAndSendFoodPartnerOrder }
                finalizeFoodPartnerOrder={ finalizeFoodPartnerOrder }
            />
        </Formik>
    );
};

export default FormWithLocationDivision;